import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { getMyStaffProfile } from '../lib/api';
import { supabase } from '../lib/supabase';
import { isMtibEmail, normalizeEmail } from '../lib/utils';
import { storage } from '../lib/storage';
import type { UserProfile } from '../types';

export interface StaffIdentity {
  subject: string | null;
  email: string | null;
  full_name: string | null;
  department_id: number | null;
  department_org: string | null;
  role: UserProfile['role'] | null;
  isAuthenticated: boolean;
  hasProfile: boolean;
  profileError: string | null;
  loading: boolean;
}

interface StaffAuthContextValue {
  identity: StaffIdentity;
  requestEmailOtp: (email: string) => Promise<void>;
  verifyEmailOtp: (email: string, token: string) => Promise<void>;
  refreshProfile: () => Promise<UserProfile | null>;
  signOut: () => Promise<void>;
}

const emptyIdentity: StaffIdentity = {
  subject: null,
  email: null,
  full_name: null,
  department_id: null,
  department_org: null,
  role: null,
  isAuthenticated: false,
  hasProfile: false,
  profileError: null,
  loading: true,
};

const StaffAuthContext = createContext<StaffAuthContextValue | null>(null);

function authenticatedMtibEmail(user: User | null): string | null {
  const email = user?.email ? normalizeEmail(user.email) : '';
  return isMtibEmail(email) ? email : null;
}

export function StaffAuthProvider({ children }: { children: React.ReactNode }) {
  const [identity, setIdentity] = useState<StaffIdentity>(emptyIdentity);
  const loadGeneration = useRef(0);
  const authenticatedIdentity = useRef<{ subject: string; email: string } | null>(null);
  const otpRequestInFlight = useRef(false);

  const loadIdentity = useCallback(async (user: User | null, options?: { preserveRoute?: boolean }) => {
    const generation = ++loadGeneration.current;
    const email = authenticatedMtibEmail(user);
    const previous = authenticatedIdentity.current;
    const next = user && email ? { subject: user.id, email } : null;
    const unchanged = Boolean(previous && next
      && previous.subject === next.subject
      && previous.email === next.email);
    if (previous && !unchanged) storage.clearCreateMeetingDraft();
    authenticatedIdentity.current = next;

    if (!user || !email) {
      if (generation === loadGeneration.current) {
        setIdentity({ ...emptyIdentity, loading: false });
      }
      return null;
    }

    if (!options?.preserveRoute || !unchanged) {
      setIdentity({
        subject: user.id,
        email,
        full_name: null,
        department_id: null,
        department_org: null,
        role: null,
        isAuthenticated: true,
        hasProfile: false,
        profileError: null,
        loading: true,
      });
    }

    try {
      const profile = await getMyStaffProfile();
      if (generation !== loadGeneration.current) return null;
      setIdentity({
        subject: user.id,
        email,
        full_name: profile?.full_name ?? null,
        department_id: profile?.department_id ?? null,
        department_org: profile?.department_org ?? null,
        role: profile?.role ?? null,
        isAuthenticated: true,
        hasProfile: Boolean(profile),
        profileError: null,
        loading: false,
      });
      return profile;
    } catch (error) {
      if (generation !== loadGeneration.current) return null;
      const message = error instanceof Error ? error.message : 'Profil pegawai tidak dapat dimuatkan. Sila cuba lagi.';
      if (options?.preserveRoute && unchanged) {
        // A best-effort refresh failure must not replace a usable mounted route
        // with the route-level profile error screen.
        return null;
      } else {
        setIdentity({
          subject: user.id,
          email,
          full_name: null,
          department_id: null,
          department_org: null,
          role: null,
          isAuthenticated: true,
          hasProfile: false,
          profileError: message,
          loading: false,
        });
      }
      return null;
    }
  }, []);

  useEffect(() => {
    let active = true;
    let authEventSeen = false;

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      authEventSeen = true;
      if (event === 'SIGNED_OUT') storage.clearCreateMeetingDraft();
      const user = session?.user ?? null;
      const email = authenticatedMtibEmail(user);
      const current = authenticatedIdentity.current;
      const unchanged = Boolean(user && email && current
        && current.subject === user.id
        && current.email === email);
      // A genuine refresh of the same normalized identity revalidates profile
      // data without switching the mounted route back to its loading screen.
      void loadIdentity(user, { preserveRoute: event === 'TOKEN_REFRESHED' && unchanged });
    });

    void supabase.auth.getUser().then(({ data }) => {
      if (active && !authEventSeen) void loadIdentity(data.user);
    }).catch(() => {
      if (active && !authEventSeen) void loadIdentity(null);
    });
    return () => {
      active = false;
      loadGeneration.current += 1;
      listener.subscription.unsubscribe();
    };
  }, [loadIdentity]);

  const requestEmailOtp = useCallback(async (rawEmail: string) => {
    if (otpRequestInFlight.current) throw new Error('Permintaan kod pengesahan sedang diproses. Sila tunggu e-mel terkini.');
    const email = normalizeEmail(rawEmail);
    if (!isMtibEmail(email)) throw new Error('Gunakan alamat e-mel pegawai @mtib.gov.my anda.');
    otpRequestInFlight.current = true;
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          // Creating an Auth identity alone grants no application access: every
          // protected RPC separately requires a matching public.users profile.
          shouldCreateUser: true,
        },
      });
      if (error) throw error;
    } finally { otpRequestInFlight.current = false; }
  }, []);

  const verifyEmailOtp = useCallback(async (rawEmail: string, rawToken: string) => {
    const email = normalizeEmail(rawEmail);
    const token = rawToken;
    if (!isMtibEmail(email)) throw new Error('Gunakan alamat e-mel pegawai @mtib.gov.my anda.');
    if (!/^\d{6,10}$/.test(token)) throw new Error('Masukkan kod pengesahan daripada e-mel anda.');

    const generationBeforeVerification = loadGeneration.current;
    const { data, error } = await supabase.auth.verifyOtp({ email, token, type: 'email' });
    if (error) throw error;
    if (!data.user) throw new Error('Kod diterima tetapi sesi pegawai tidak dikembalikan.');
    // Supabase normally emits SIGNED_IN during verifyOtp. Only hydrate directly
    // when no newer Auth event has already taken ownership of session state.
    if (generationBeforeVerification === loadGeneration.current) {
      await loadIdentity(data.user);
    }
  }, [loadIdentity]);

  const refreshProfile = useCallback(async () => {
    const generationBeforeRefresh = loadGeneration.current;
    const { data, error } = await supabase.auth.getUser();
    if (error) throw error;
    if (generationBeforeRefresh !== loadGeneration.current) return null;
    return loadIdentity(data.user);
  }, [loadIdentity]);

  const signOut = useCallback(async () => {
    storage.clearCreateMeetingDraft();
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    await loadIdentity(null);
  }, [loadIdentity]);

  const value = useMemo(() => ({ identity, requestEmailOtp, verifyEmailOtp, refreshProfile, signOut }), [identity, requestEmailOtp, verifyEmailOtp, refreshProfile, signOut]);
  return <StaffAuthContext.Provider value={value}>{children}</StaffAuthContext.Provider>;
}

export function useStaffAuth() {
  const value = useContext(StaffAuthContext);
  if (!value) throw new Error('useStaffAuth must be used inside StaffAuthProvider');
  return value;
}
